'use client';

/**
 * Data + filter state hook for the schoolhouse browse page.
 *
 * Module: app/schoolhouse/use-schoolhouses
 * Layer:  hook
 *
 * Purpose:
 *     Encapsulates schoolhouse list fetching and the search/featured/sort
 *     filter state for the browse page, keeping the page component small so
 *     every unit clears the CC<=5 / <=60-line gates. Filter state is local UI
 *     state; the active query drives a debounced, abortable reload.
 *
 * AI context:
 *     - Entry points called by: src/app/schoolhouse/page.tsx.
 *     - Depends on: lib/schoolhouse fetchSchoolhouses.
 *     - Side effects: network I/O (cancelled on dependency change/unmount).
 *     - Invariants: every load is keyed off (activeQuery, featuredOnly,
 *       sortBy) and aborts the prior request; failures resolve to `[]` inside
 *       the helper, never thrown to React.
 *     - Replacement cost: LOW.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  type SchoolhouseSortBy,
  type SchoolhouseSummary,
  fetchSchoolhouses,
} from '@/lib/schoolhouse';

/** Everything the browse page needs to render and react to user input. */
export interface SchoolhousesController {
  readonly schools: readonly SchoolhouseSummary[];
  readonly loading: boolean;
  readonly draftQuery: string;
  readonly activeQuery: string;
  readonly featuredOnly: boolean;
  readonly sortBy: SchoolhouseSortBy;
  readonly hasFilters: boolean;
  readonly setDraftQuery: (next: string) => void;
  readonly submitSearch: () => void;
  readonly toggleFeatured: () => void;
  readonly setSortBy: (next: SchoolhouseSortBy) => void;
  readonly clearFilters: () => void;
}

/** Loaded list plus its in-flight flag. */
interface SchoolhousesData {
  readonly schools: readonly SchoolhouseSummary[];
  readonly loading: boolean;
}

/**
 * Load schoolhouses for the active filters, cancelling stale requests.
 *
 * @param query - Active (submitted) search term.
 * @param featuredOnly - Whether to restrict to featured schoolhouses.
 * @param sortBy - Active sort key.
 * @returns The loaded list plus a loading flag.
 */
function useSchoolhousesData(
  query: string,
  featuredOnly: boolean,
  sortBy: SchoolhouseSortBy,
): SchoolhousesData {
  const [schools, setSchools] = useState<readonly SchoolhouseSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const run = async (): Promise<void> => {
      setLoading(true);
      const params = { search: query, featuredOnly, sortBy };
      const result = await fetchSchoolhouses(params, controller.signal);
      if (!controller.signal.aborted) {
        setSchools(result);
        setLoading(false);
      }
    };
    void run();
    return (): void => {
      controller.abort();
    };
  }, [query, featuredOnly, sortBy]);

  return { schools, loading };
}

/**
 * Browse-page controller: list data plus search/featured/sort filter state.
 *
 * @returns The {@link SchoolhousesController} consumed by the page.
 */
export function useSchoolhouses(): SchoolhousesController {
  const [draftQuery, setDraftQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SchoolhouseSortBy>('featured');
  const data = useSchoolhousesData(activeQuery, featuredOnly, sortBy);

  const submitSearch = useCallback((): void => {
    setActiveQuery(draftQuery.trim());
  }, [draftQuery]);

  const toggleFeatured = useCallback((): void => {
    setFeaturedOnly((prev) => !prev);
  }, []);

  const clearFilters = useCallback((): void => {
    setDraftQuery('');
    setActiveQuery('');
    setFeaturedOnly(false);
    setSortBy('featured');
  }, []);

  return {
    schools: data.schools,
    loading: data.loading,
    draftQuery,
    activeQuery,
    featuredOnly,
    sortBy,
    hasFilters: activeQuery !== '' || featuredOnly,
    setDraftQuery,
    submitSearch,
    toggleFeatured,
    setSortBy,
    clearFilters,
  };
}
